/**
 * RTK Query Base API Configuration
 * 
 * This is the foundation for all API calls in the application.
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401 errors
 * - Global error handling
 * - Request/response logging in development
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '../store/storeTypes';
import { env } from '@app/config/env';
import { refreshKeycloakToken } from '@core/auth/keycloak';
import { clearAuth, setToken } from '@core/auth/store/authSlice';

const getTokenExpiryMs = (tokenParsed: unknown): number => {
  const expSeconds =
    typeof tokenParsed === 'object' && tokenParsed !== null && 'exp' in tokenParsed
      ? (tokenParsed as { exp?: unknown }).exp
      : undefined;

  return typeof expSeconds === 'number' ? expSeconds * 1000 : Date.now() + 60_000;
};

// Endpoints that upload multipart/form-data; Content-Type must be left
// unset so the browser can generate the proper boundary.
const MULTIPART_ENDPOINTS = new Set<string>([
  'previewFuelLogImport',
  'commitFuelLogImport',
  'importOfficialJobsCatalog',
  'uploadDocumentTemplate',
  'uploadEmployeeDocument',
  'uploadEmployeeDocumentFinal',
  'saveEmployeeDocumentDraft',
  'uploadArchiveDocument',
]);

// Base query with auth token injection
const baseQuery = fetchBaseQuery({
  baseUrl: env.apiUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    // Get token from auth state
    const token = (getState() as RootState).auth.token;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Set content type (skip for multipart uploads so the browser sets the boundary)
    if (!MULTIPART_ENDPOINTS.has(endpoint) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  },
});

// Base query with automatic token refresh
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Try the request
  let result = await baseQuery(args, api, extraOptions);

  // If 401 Unauthorized, try to refresh token via Keycloak
  if (result.error && result.error.status === 401) {
    try {
      const refreshed = await refreshKeycloakToken(30);

      if (refreshed.token && refreshed.refreshToken) {
        const tokenExpiry = getTokenExpiryMs(refreshed.tokenParsed);

        api.dispatch(
          setToken({
            token: refreshed.token,
            refreshToken: refreshed.refreshToken,
            tokenExpiry,
          })
        );

        // Retry the original request with new token
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(clearAuth());
      }
    } catch {
      api.dispatch(clearAuth());
    }
  }

  // Log errors in development
  if (import.meta.env.DEV && result.error) {
    console.error('API Error:', {
      endpoint: typeof args === 'string' ? args : args.url,
      error: result.error,
    });
  }

  return result;
};

/**
 * Base API instance
 *
 * All module-specific APIs should inject endpoints into this base API.
 * Base URL is configured to include /api prefix (e.g., http://localhost:5000/api)
 *
 * URL Pattern: /api/{module}/{resource}
 * Examples:
 *   - /api/fleets/vehicles
 *   - /api/users/profile
 *   - /api/hr/employees
 *
 * Example:
 * ```typescript
 * export const fleetsApi = baseApi.injectEndpoints({
 *   endpoints: (builder) => ({
 *     getVehicles: builder.query<Vehicle[], void>({
 *       query: () => '/fleets/vehicles',  // Full path: /api/fleets/vehicles
 *     }),
 *   }),
 * });
 * ```
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    // Fleet module tags
    'Vehicle',
    'VehicleBlock',
    'FuelLog',
    'TravelOrder',
    'TravelOrderPeriod',
    'VehicleRequest',
    'VehicleAssignments',
    'Registration',
    'Service',
    'Provider',
    'Contract',
    'FleetUsers',
    'Tire',
    'OperationalUnit',
    'WorkOrder',
    'FleetReportTemplate',
    // Users module tags
    'User',
    'Profile',
    'Role',
    'Permission',
    'EmployeeAccountCandidate',
    'EmployeeAccountStatus',
    // HR module tags
    'Employee',
    'Department',
    'WorkUnit',
    'EmployeeLookup',
    'WorkUnitLookup',
    'JobPosition',
    'EmployeeAssignment',
    // HR catalogs
    'EducationQualification',
    'EmploymentType',
    'AbsenceType',
    'EmploymentStartReason',
    'EmploymentEndReason',
    'OfficialJob',
    'EmploymentPeriod',
    'EmployeeDocumentsProfile',
    'EmployeeOriginProfile',
    'EmployeeEducation',
    'EmployeePreviousSeniority',
    'EmployeeSeniorityEntry',
    'LeaveEntitlementRule',
    'PaidLeaveCriterion',
    'EmployeeLeaveEntitlementProfile',
    'EmployeeLeaveEntitlement',
    'EmployeeAbsence',
    'EmployeeFamilyMember',
    'Nationality',
    'Citizenship',
    'Religion',
    'PatronSaint',
    'PublicHoliday',
    'DocumentType',
    'DocumentTemplate',
    'EmployeeDocument',
    'ReportTemplate',
    // DocumentArchive module tags
    'ArchiveDocument',
    'ArchiveDocumentPermission',
    'ArchiveCatalog',
  ],
  endpoints: () => ({}),
});
